/**
 * Énfasis de las 12 semanas.
 *
 * TODOS los componentes están presentes desde la Semana 1. No hay fases donde
 * falte la pragmática durante seis semanas. Solo cambia el ÉNFASIS, y los
 * bloques no son compartimentos rígidos.
 *
 * CAMBIO PEDAGÓGICO 4 (aprobado): la spec define los cuatro bloques pero no
 * define ningún mecanismo, y v2_2 no los implementaba en absoluto. Si el énfasis
 * significara sesiones distintas por bloque, el contenido se multiplicaría por
 * cuatro. Se implementa como capa ligera:
 *
 *   a) el bloque actual, visible en Hoy y en Plan;
 *   b) `lineaPrompt`, que se añade al prompt de ChatGPT durante el bloque;
 *   c) un peso de 0.5 hacia las áreas del bloque en la puntuación del Día 5.
 */

import type { BloqueEnfasis } from '../tipos.js';

export const BLOQUES_ENFASIS: BloqueEnfasis[] = [
  {
    desdeSemana: 1,
    hastaSemana: 3,
    titulo: 'Romper los scripts',
    descripcion:
      'Más énfasis en salir de las respuestas conocidas, en los targets que salieron de tu baseline, en listening y en percepción y pronunciación. La pragmática está presente desde el Día 1.',
    areas: ['habla_espontanea', 'listening', 'pronunciacion_prosodia'],
    lineaPrompt:
      'Context for this block: I am in weeks 1-3 of the program. Push me slightly harder on speaking without a script and on understanding natural speech, and keep an eye on the pronunciation targets I mention. Do not turn this into a pronunciation lesson.',
  },
  {
    desdeSemana: 4,
    hastaSemana: 6,
    titulo: 'Recuperar más rápido',
    descripcion:
      'Más énfasis en recuperación léxica, en repetir tareas con variación, en reformular y transferir, y en automatizar sin volverse rígido.',
    areas: ['recuperacion_lexica', 'gramatica_fluidez'],
    lineaPrompt:
      'Context for this block: I am in weeks 4-6 of the program. Push me slightly harder on retrieving words and structures quickly, on reformulating, and on repeating a task with variation instead of repeating it identically.',
  },
  {
    desdeSemana: 7,
    hastaSemana: 9,
    titulo: 'Sonar más natural',
    descripcion:
      'Más énfasis en prosodia, connected speech, naturalidad, pragmática y turn-taking.',
    areas: ['pronunciacion_prosodia', 'naturalidad', 'interaccion_turnos'],
    lineaPrompt:
      'Context for this block: I am in weeks 7-9 of the program. Push me slightly harder on rhythm, connected speech, word choice that sounds natural, and on how I take and give turns. Keep corrections few and high-impact.',
  },
  {
    desdeSemana: 10,
    hastaSemana: 12,
    titulo: 'Conversación real e impredecible',
    descripcion:
      'Más énfasis en conversaciones impredecibles, interacción naturalista, repair, misunderstanding, presión comunicativa moderada y transferencia a contextos nuevos.',
    areas: ['interaccion_turnos', 'habla_espontanea', 'naturalidad'],
    lineaPrompt:
      'Context for this block: I am in weeks 10-12 of the program. Push me slightly harder with unpredictability, misunderstandings I have to repair, and moderate communicative pressure. Change contexts more often than usual.',
  },
];

/**
 * Bloque de énfasis de una semana dada.
 *
 * Fuera del rango 1-12 devuelve `null`: el programa dura doce semanas y la
 * aplicación no debe inventar un énfasis para la semana 13. Si el usuario sigue
 * después de la 12, sigue sin énfasis, que es honesto.
 */
export function bloqueDeSemana(semana: number): BloqueEnfasis | null {
  return (
    BLOQUES_ENFASIS.find((b) => semana >= b.desdeSemana && semana <= b.hastaSemana) ?? null
  );
}
